import React, { useState, useEffect, useRef } from 'react';
import { documentAPI } from '../services/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { 
  Upload, 
  FileText, 
  Image, 
  File,
  Download, 
  Trash2, 
  Loader2,
  FolderOpen,
  Plus,
  X,
  FileImage,
  FileSpreadsheet,
  Eye
} from 'lucide-react';
import { toast } from 'sonner';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
};

const getFileIcon = (fileType) => {
  if (fileType?.startsWith('image/')) return <FileImage className="w-5 h-5 text-purple-400" />;
  if (fileType === 'application/pdf') return <FileText className="w-5 h-5 text-red-400" />;
  if (fileType?.includes('spreadsheet') || fileType?.includes('excel')) return <FileSpreadsheet className="w-5 h-5 text-green-400" />;
  return <File className="w-5 h-5 text-blue-400" />;
};

const getCategoryColor = (category) => {
  const colors = {
    'Contract': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    'Inspection': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'Photo': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'Receipt': 'bg-green-500/20 text-green-400 border-green-500/30',
    'Permit': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    'Insurance': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    'Appraisal': 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    'Title': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    'Other': 'bg-slate-500/20 text-slate-400 border-slate-500/30',
  };
  return colors[category] || colors['Other'];
};

const CATEGORIES = ['Contract', 'Inspection', 'Photo', 'Receipt', 'Permit', 'Insurance', 'Appraisal', 'Title', 'Other'];

const DocumentVault = ({ projectId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    category: 'Other',
    description: ''
  });
  const [downloading, setDownloading] = useState(null);
  const [deleting, setDeleting] = useState(null);
  const [filterCategory, setFilterCategory] = useState('All');
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (projectId) {
      fetchDocuments();
    }
  }, [projectId]);

  const fetchDocuments = async () => {
    try {
      const response = await documentAPI.getAll(projectId);
      setDocuments(response.data || []);
    } catch (error) {
      console.error('Fetch documents error:', error);
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (10MB limit)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File too large. Maximum size is 10MB');
        return;
      }
      setSelectedFile(file);
      setUploadDialogOpen(true);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('project_id', projectId);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);
      
      await documentAPI.upload(formData);
      
      toast.success('Document uploaded successfully!');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setUploadForm({ category: 'Other', description: '' });
      fetchDocuments();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    setDownloading(doc.id);
    
    try {
      const response = await documentAPI.download(doc.id);
      
      const blob = new Blob([response.data], { type: doc.file_type });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Document downloaded!');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download document');
    } finally {
      setDownloading(null);
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This cannot be undone.`)) return;
    
    setDeleting(doc.id);
    
    try {
      await documentAPI.delete(doc.id);
      toast.success('Document deleted');
      fetchDocuments();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error('Failed to delete document');
    } finally {
      setDeleting(null);
    }
  };

  const filteredDocuments = filterCategory === 'All' 
    ? documents 
    : documents.filter(d => d.category === filterCategory);

  const documentsByCategory = documents.reduce((acc, doc) => {
    acc[doc.category] = (acc[doc.category] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderOpen className="w-5 h-5 text-primary" />
          <h3 className="font-semibold">Document Vault</h3>
          <Badge variant="outline" className="rounded-sm">
            {documents.length} files
          </Badge>
        </div>
        <Button 
          onClick={() => fileInputRef.current?.click()}
          size="sm"
          className="bg-primary text-primary-foreground rounded-sm"
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          className="hidden"
          onChange={handleFileSelect}
          accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv"
        />
      </div>

      {/* Category Filter */}
      {documents.length > 0 && (
        <div className="flex flex-wrap gap-2">
          <Button
            variant={filterCategory === 'All' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilterCategory('All')}
            className="rounded-sm text-xs"
          >
            All ({documents.length})
          </Button>
          {CATEGORIES.filter(cat => documentsByCategory[cat]).map(category => (
            <Button
              key={category}
              variant={filterCategory === category ? 'default' : 'outline'}
              size="sm"
              onClick={() => setFilterCategory(category)}
              className="rounded-sm text-xs"
            >
              {category} ({documentsByCategory[category]})
            </Button>
          ))}
        </div>
      )}

      {/* Documents List */}
      {filteredDocuments.length > 0 ? (
        <div className="space-y-2">
          {filteredDocuments.map((doc) => (
            <div 
              key={doc.id}
              className="flex items-center justify-between p-3 bg-secondary/30 rounded-sm border border-border hover:border-primary/30 transition-colors"
            >
              <div className="flex items-center gap-3 min-w-0">
                {getFileIcon(doc.file_type)}
                <div className="min-w-0">
                  <p className="font-medium text-sm truncate">{doc.name}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Badge variant="outline" className={`rounded-sm text-xs ${getCategoryColor(doc.category)}`}>
                      {doc.category}
                    </Badge>
                    <span>{formatFileSize(doc.file_size)}</span>
                    <span>•</span>
                    <span>{formatDate(doc.created_at)}</span>
                  </div>
                  {doc.description && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">{doc.description}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(doc)}
                  disabled={downloading === doc.id}
                  className="rounded-sm h-8 w-8 p-0"
                >
                  {downloading === doc.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDelete(doc)}
                  disabled={deleting === doc.id}
                  className="rounded-sm h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                >
                  {deleting === doc.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-8 border border-dashed border-border rounded-sm">
          <FolderOpen className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
          <p className="text-sm text-muted-foreground">
            {filterCategory === 'All' ? 'No documents uploaded yet' : `No ${filterCategory} documents`}
          </p>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => fileInputRef.current?.click()}
            className="mt-3 rounded-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Upload First Document
          </Button>
        </div>
      )}

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="bg-card border-border rounded-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Upload Document</DialogTitle>
            <DialogDescription>
              Add details for your document
            </DialogDescription>
          </DialogHeader>
          
          {selectedFile && (
            <div className="space-y-4">
              {/* Selected File Preview */}
              <div className="p-3 bg-secondary/30 rounded-sm flex items-center gap-3">
                {getFileIcon(selectedFile.type)}
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-sm truncate">{selectedFile.name}</p>
                  <p className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedFile(null);
                    setUploadDialogOpen(false);
                  }}
                  className="rounded-sm h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>

              {/* Category Selection */}
              <div>
                <Label>Category</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {CATEGORIES.map(category => (
                    <Button
                      key={category}
                      type="button"
                      variant={uploadForm.category === category ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setUploadForm({ ...uploadForm, category })}
                      className="rounded-sm text-xs"
                    >
                      {category}
                    </Button>
                  ))}
                </div>
              </div>

              {/* Description */}
              <div>
                <Label htmlFor="description">Description (optional)</Label>
                <Input
                  id="description"
                  placeholder="Brief description of this document..."
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                  className="bg-secondary border-border rounded-sm mt-1"
                />
              </div>

              {/* Upload Button */}
              <div className="flex justify-end gap-2 pt-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedFile(null);
                    setUploadDialogOpen(false);
                  }}
                  className="rounded-sm"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleUpload}
                  disabled={uploading}
                  className="bg-primary text-primary-foreground rounded-sm"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4 mr-2" />
                      Upload Document
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentVault;
